/**
 * Copyright 2021 (c) Christian von Arnim, ISW University of Stuttgart (for umati and VDW e.V.)
 * Copyright 2021-2022 (c) Karsten Röthig, FVA GmbH (for umati and VDW e.V.)
 * Copyright 2023 (c) Sebastian Friedl, FVA GmbH (for umati and VDW e.V.)
 */
import { Template } from '@umati/dashboard-types';
import ControllerTypeJson from './templates/_ControllerType.json';
import GlassMachineTypeJson from './templates/_GlassMachineType.json';
import GMSTypeJson from './templates/_GMSType.json';
import MachineToolTypeJson from './templates/_MachineToolType.json';
import MotionDeviceSystemTypeJson from './templates/_MotionDeviceSystemType.json';
import MotionDeviceTypeJson from './templates/_MotionDeviceType.json';
import TighteningSystemTypeJson from './templates/_TighteningSystemType.json';
import JoiningSystemTypeJson from './templates/_JoiningSystemType.json';
import UmatiPlasticsRubberGenericTypeJson from './templates/_UmatiPlasticsRubberGenericType.json';
import IMM_MES_InterfaceTypeJson from './templates/_IMM_MES_InterfaceType.json';
import LDS_InterfaceTypeJson from './templates/_LDS_InterfaceType.json';
import HRD_InterfaceTypeJson from './templates/_HRD_InterfaceType.json';
import TCD_InterfaceTypeJson from './templates/_TCD_InterfaceType.json';
import Extruder_InterfaceTypeJson from './templates/_Extruder_InterfaceType.json';
import Corrugator_InterfaceTypeJson from './templates/_Corrugator_InterfaceType.json';
import ImmRobotCellTypeJson from './templates/_ImmRobotCellType.json';
import Cutter_InterfaceTypeJson from './templates/_Cutter_InterfaceType.json';
import VisionSystemAssetTypeJson from './templates/_VisionSystemAssetType.json';
import AdditiveManufacturingTypeJson from './templates/_AdditiveManufacturingType.json';
import DemonstratorTypeJson from './templates/_DemonstratorType.json';
import TextileTestingDeviceType from './templates/_TextileTestingDeviceType.json';
import WwMachineTypeJson from './templates/_WwMachineType.json';
import WireHarnessMachine from './templates/_WireHarnessMachineType.json';
import LaserSystemTypeJson from './templates/_LaserSystemType.json';
import _PieceCountingScaleTypeJson from './templates/_PieceCountingScaleType.json';
import DMGMORIMachineToolTypeJson from './templates/_DMGMORI_MachineToolType.json';
import HaulageMachineTypeJson from './templates/_HaulageMachineType.json';

export interface ISpecification {
  ShortId: string;
  /// At least one element
  TypeDefinitions: [ITypeDefinition, ...ITypeDefinition[]];
  DisplayName: string;
}

interface ITypeDefinition {
  Type: string;
  Template?: Template;
}

export const Specifications: ISpecification[] = [
  {
    ShortId: 'MT',
    DisplayName: 'Machine Tools',
    TypeDefinitions: [
      {
        Type: 'MachineToolType',
        Template: MachineToolTypeJson,
      },
      {
        Type: 'DMGMORI_MachineToolType',
        Template: DMGMORIMachineToolTypeJson,
      },
    ],
  },
  {
    ShortId: 'Wire',
    DisplayName: 'WireHarness',
    TypeDefinitions: [
      {
        Type: 'WireHarnessMachineType',
        Template: WireHarnessMachine,
      }
    ],
  },
  {
    ShortId: 'ST',
    DisplayName: 'Surface Technology',
    TypeDefinitions: [
      {
        Type: 'SurfaceTechnologyVesselType',
      },
      {
        Type: 'SurfaceTechnologyValveType',
      },
      {
        Type: 'SurfaceTechnologyPumpType',
      },
      {
        Type: 'SurfaceTechnologyObjectType',
      },
      {
        Type: 'SurfaceTechnologyFilterSystemType',
      },
      {
        Type: 'SurfaceTechnologyDosingSystemType',
      },
      {
        Type: 'SurfaceTechnologyCoatingSystemType',
      },
    ],
  },
  {
    ShortId: 'WW',
    DisplayName: 'Wood Working',
    TypeDefinitions: [
      {
        Type: 'WwMachineType',
        Template: WwMachineTypeJson,
      },
    ],
  },
  {
    ShortId: 'RO',
    DisplayName: 'Robotics',
    TypeDefinitions: [
      {
        Type: 'ControllerType',
        Template: ControllerTypeJson,
      },
      {
        Type: 'MotionDeviceType',
        Template: MotionDeviceTypeJson,
      },
      {
        Type: 'MotionDeviceSystemType',
        Template: MotionDeviceSystemTypeJson,
      },
    ],
  },
  {
    ShortId: 'MV',
    DisplayName: 'MachineVision',
    TypeDefinitions: [
      {
        Type: 'VisionSystemAssetType',
        Template: VisionSystemAssetTypeJson,
      },
    ],
  },
  {
    ShortId: 'GM',
    DisplayName: 'Geometrical Measuring Systems',
    TypeDefinitions: [
      {
        Type: 'GMSType',
        Template: GMSTypeJson,
      },
    ],
  },
  {
    ShortId: 'DEMO',
    DisplayName: 'Demonstrator',
    TypeDefinitions: [
      {
        Type: 'DemonstratorType',
        Template: DemonstratorTypeJson,
      },
    ],
  },
  {
    ShortId: 'TTD',
    DisplayName: 'Textile Testing Device',
    TypeDefinitions: [
      {
        Type: 'TextileTestingDeviceType',
        Template: TextileTestingDeviceType,
      },
    ],
  },
  {
    ShortId: 'IJT',
    DisplayName: 'Industrial Joining Technologies Tightening Systems',
    TypeDefinitions: [
      {
        Type: 'TighteningSystemType',
        Template: TighteningSystemTypeJson,
      },
      {
        Type: 'JoiningSystemType',
        Template: JoiningSystemTypeJson
      },
    ],
  },
  {
    ShortId: 'PRIMMMES',
    DisplayName: 'Plastics & Rubber - OPC 40077 - IMM 2 MES',
    TypeDefinitions: [
      {
        Type: 'IMM_MES_InterfaceType',
        Template: IMM_MES_InterfaceTypeJson,
      },
    ],
  },
  {
    ShortId: 'PRIMMROB',
    DisplayName: 'Plastics & Rubber - OPC 40079 - IMM 2 Robot',
    TypeDefinitions: [
      {
        Type: 'ImmRobotCellType',
        Template: ImmRobotCellTypeJson,
      },
    ],
  },
  {
    ShortId: 'PRTCD',
    DisplayName: 'Plastics & Rubber - OPC 40082-1 - TCD',
    TypeDefinitions: [
      {
        Type: 'TCD_InterfaceType',
        Template: TCD_InterfaceTypeJson,
      },
    ],
  },
  {
    ShortId: 'PRHRD',
    DisplayName: 'Plastics & Rubber - OPC 40082-2 - HRD',
    TypeDefinitions: [
      {
        Type: 'HRD_InterfaceType',
        Template: HRD_InterfaceTypeJson,
      },
    ],
  },
  {
    ShortId: 'PRLSR',
    DisplayName: 'Plastics & Rubber - OPC 40082-3 - LSR',
    TypeDefinitions: [
      {
        Type: 'LDS_InterfaceType',
        Template: LDS_InterfaceTypeJson,
      },
    ],
  },
  {
    ShortId: 'PREXT',
    DisplayName: 'Plastics & Rubber - OPC 40084-3 - EXTRUDER',
    TypeDefinitions: [
      {
        Type: 'Extruder_InterfaceType',
        Template: Extruder_InterfaceTypeJson,
      },
    ],
  },
  {
    ShortId: 'PRCUT',
    DisplayName: 'Plastics & Rubber - OPC 40084-9 - CUTTER',
    TypeDefinitions: [
      {
        Type: 'Cutter_InterfaceType',
        Template: Cutter_InterfaceTypeJson,
      },
    ],
  },
  {
    ShortId: 'PRCORR',
    DisplayName: 'Plastics & Rubber - OPC 40084-11 - CORRUGATOR',
    TypeDefinitions: [
      {
        Type: 'Corrugator_InterfaceType',
        Template: Corrugator_InterfaceTypeJson,
      },
    ],
  },
  {
    ShortId: 'PRGEN',
    DisplayName: 'Plastics & Rubber - GENERIC',
    TypeDefinitions: [
      {
        Type: 'UmatiPlasticsRubberGenericType',
        Template: UmatiPlasticsRubberGenericTypeJson,
      },
    ],
  },
  {
    ShortId: 'AM',
    DisplayName: 'Additive Manufacturing',
    TypeDefinitions: [
      {
        Type: 'AdditiveManufacturingType',
        Template: AdditiveManufacturingTypeJson,
      },
    ],
  },
  {
    ShortId: 'FG',
    DisplayName: 'Flat Glass Processing',
    TypeDefinitions: [
      {
        Type: 'GlassMachineType',

        Template: GlassMachineTypeJson
      },
    ],
  }, 
  {
    ShortId: 'Scales',
    DisplayName: 'Scales',
    TypeDefinitions: [
      {
        Type: 'PieceCountingScaleType',

        Template: _PieceCountingScaleTypeJson
      },
    ],
  },
  {
    ShortId: 'MN',
    DisplayName: 'Mining',
    TypeDefinitions: [
      {
        Type: 'HaulageMachineType',
        Template: HaulageMachineTypeJson
      },
    ],
  },
  {
    ShortId: 'Laser',
    DisplayName: 'Laser System',
    TypeDefinitions: [
      {
        Type: 'LaserSystemType',
        Template: LaserSystemTypeJson
      }
    ]
  }
];

export const getSpecificationByShortId = (shortId: string) => {
  return Specifications
    .find((spec) => spec.ShortId === shortId);
};
